/**
 * OfflineBanner.tsx
 *
 * 全局離線狀態橫幅組件。
 *
 * 顯示規則：
 * - 在線且無 pending / failed 條目：不渲染（return null）
 * - 離線：顯示橙色警告橫幅，提示當前離線
 * - 在線但有 pending 條目：顯示藍色信息橫幅，附「立即同步」按鈕
 * - 有 failed 條目（重試耗盡 / 409 衝突）：顯示橙色警告，附「前往處理」直達離線管理頁
 *
 * 掛載位置：建議放在 Layout 的頂部，如 AdminLayout / AuditorLayout / CounselorLayout 內。
 *
 * 使用範例：
 * ```tsx
 * <OfflineBanner />
 * <main>{children}</main>
 * ```
 */

import { Alert, Button, Space } from 'antd';
import { Link } from '@tanstack/react-router';
import { AlertTriangle, Cloud, CloudOff, RefreshCw } from 'lucide-react';
import { useOfflineState } from '@/pwa/use-offline-state';

/**
 * 離線狀態橫幅。在線且無待同步/失敗項目時不渲染。
 */
export function OfflineBanner() {
  const { online, pending, failed, syncing, triggerSync } = useOfflineState();

  // 在線且無 pending / failed：靜默，不佔用任何 UI 空間
  if (online && pending === 0 && failed === 0) return null;

  // 有失敗項時整條橫幅升級為 warning（比「待同步」更需要用戶注意）
  const type = !online || failed > 0 ? 'warning' : 'info';

  return (
    <Alert
      type={type}
      banner
      icon={
        failed > 0 ? (
          <AlertTriangle size={16} />
        ) : online ? (
          <Cloud size={16} />
        ) : (
          <CloudOff size={16} />
        )
      }
      message={
        <Space>
          {/* 離線狀態標識 */}
          {!online && <span>離線中</span>}

          {/* 待同步條目計數 */}
          {pending > 0 && <span>{pending} 項待同步</span>}

          {/* 同步失敗計數：重試耗盡或衝突，需到離線管理頁人工處理 */}
          {failed > 0 && <span>{failed} 項同步失敗</span>}

          {/* 在線但有 pending 時顯示手動同步按鈕 */}
          {online && pending > 0 && (
            <Button
              size="small"
              type="link"
              loading={syncing}
              onClick={triggerSync}
              icon={<RefreshCw size={14} />}
            >
              立即同步
            </Button>
          )}

          {/* 有失敗項時直達離線管理頁的「同步失敗項」卡片處理 */}
          {online && failed > 0 && (
            <Link to="/me/storage">
              <Button size="small" type="link">
                前往處理
              </Button>
            </Link>
          )}
        </Space>
      }
    />
  );
}
